import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchFacility } from '@/lib/api';
import SEO from '@/components/SEO';
import { MapPin, Clock, Users, Target, Heart, Shield } from 'lucide-react';

const MISSION_VALUES = [
  { id: 'purpose', icon: Target, label: 'Purpose-Built', desc: 'Designed from the ground up for multi-sport excellence' },
  { id: 'community', icon: Users, label: 'Community First', desc: 'Serving Apex families and athletes since 2026' },
  { id: 'welcome', icon: Heart, label: 'All Welcome', desc: 'Programs for every age and skill level' },
  { id: 'safe', icon: Shield, label: 'Safe & Clean', desc: 'Climate-controlled, professionally maintained' },
];

const OFFERINGS = [
  { id: 'tunnels', title: 'Batting Tunnels', desc: '6 indoor tunnels for cricket, baseball, and softball', link: '/batting-tunnels' },
  { id: 'soccer', title: 'Soccer / Futsal', desc: 'Indoor courts for open play, leagues, and events', link: '/soccer-futsal' },
  { id: 'parties', title: 'Birthday Parties', desc: 'All-inclusive sports party packages for kids', link: '/parties' },
  { id: 'tournaments', title: 'Tournaments', desc: 'Box cricket, pickleball, and futsal tournaments', link: '/tournaments' },
];

export default function About() {
  const [facility, setFacility] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchFacility().then(setFacility).catch(() => {}); }, []);

  return (
    <div data-testid="about-page" className="pt-[68px]">
      <SEO title="About" description="Learn about Propel Sports NC — Apex's premier indoor multi-sport facility founded in 2026." path="/about" />

      <section className="relative py-24 bg-[#0B1D3A] overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(ellipse 600px 400px at 50% 50%, rgba(242,101,34,0.25) 0%, transparent 70%)'
        }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">About Us</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            {facility?.description || "An indoor multi-sport facility founded by community-minded individuals. We provide the space for parties, batting tunnels, soccer, and tournaments."}
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#0B1D3A] mb-4">Our Mission</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                At Propel Sports NC, we believe every athlete deserves a world-class indoor facility. Founded in 2026, we built Apex's first dedicated multi-sport indoor home to serve players of all ages and skill levels.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Whether you're a young batter working on your swing, a futsal team prepping for tournament season, or a family celebrating a birthday, our facility is designed to be your home base for sports and community.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {MISSION_VALUES.map((item) => (
                <div key={item.id} className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-center hover:border-[#F26522]/30 hover:shadow-md transition-all">
                  <div className="w-10 h-10 rounded-lg bg-[#F26522]/10 flex items-center justify-center mx-auto mb-2">
                    <item.icon size={20} className="text-[#F26522]" />
                  </div>
                  <div className="text-[#0B1D3A] font-semibold text-sm mb-0.5">{item.label}</div>
                  <div className="text-gray-500 text-xs">{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-[#0B1D3A] mb-8">What We Offer</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {OFFERINGS.map((item) => (
              <Link key={item.id} to={item.link} className="bg-white border border-gray-200 rounded-xl p-6 hover:border-[#F26522]/30 hover:shadow-lg transition-all text-left group">
                <h3 className="text-[#0B1D3A] font-bold mb-1 group-hover:text-[#F26522] transition-colors">{item.title}</h3>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Location + Hours */}
      {facility && (
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-[#0B1D3A] mb-6">Visit Us</h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 mb-8">
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin size={18} className="text-[#F26522]" />
                <span className="text-sm">810 Center St, Building #4, Apex NC, 27502</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Clock size={18} className="text-[#F26522]" />
                <span className="text-sm">Open 7 days a week</span>
              </div>
            </div>
            <Link to="/facility" className="inline-flex items-center gap-2 bg-[#F26522] hover:bg-[#D9551A] text-white px-8 py-3 rounded-lg font-semibold text-sm transition-all">
              View Full Hours & Details
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
